import QueryString from 'query-string';

import Services, { HeaderUTF8Option } from './index';
import {
  MANAGE_USERS_URL,
  ADMIN_SERVLET_URL,
  USER_SERVLET_URL,
  V2_MFA_STATUS_URL,
  MOVE_CREDIT_URL,
  AUTHENTICATED_URL,
  ERROR_LIST_OPTIONS_URL,
} from 'consts/api';
import { SecurityConst } from 'consts/data';
import { IsJsonString } from 'helpers';
import { isEmpty } from 'lodash';

export const keyRootUser = 'rootUser';
export const keyCachedUsers = 'cachedUsers';

// Because the field name will be failed to parse (because of []). Add dummyValue for keep the 'searchFilters[]': "username" not changed
class UserServices {
  clearUserCached() {
    localStorage.removeItem(SecurityConst.authStorage);
    localStorage.removeItem(keyRootUser);
    Services.setCookie('sessionId=');
    // localStorage.removeItem('theme');
  }

  getRootUser() {
    return localStorage.getItem(keyRootUser);
  }

  setRootUser(email) {
    localStorage.setItem(keyRootUser, email);
  }

  getCachedUser() {
    return localStorage.getItem(keyCachedUsers) &&
      IsJsonString(localStorage.getItem(keyCachedUsers))
      ? JSON.parse(localStorage.getItem(keyCachedUsers))
      : undefined;
  }

  updateCachedUser(user) {
    if (isEmpty(user)) {
      return;
    }

    localStorage.setItem(keyCachedUsers, JSON.stringify(user));
  }

  /**
   *
   * @returns {{
   *  jwt: string
   * }}
   */
  getJWTAuth() {
    const jwtStringify = localStorage.getItem(SecurityConst.authStorage) || '';
    if (IsJsonString(jwtStringify)) {
      return JSON.parse(jwtStringify);
    }

    return null;
  }

  get2MFA() {
    return Services.get(V2_MFA_STATUS_URL);
  }

  update2MFA(status) {
    return Services.post(`${V2_MFA_STATUS_URL}?isEnableMfa=${status}`);
  }

  getListUser({ action, filters }) {
    const {
      searchFilters,
      searchValues,
      sortDirections,
      sortFields,
      pageSize,
      page,
      operator,
      userStatuses,
    } = filters;

    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action,
        'sortFields[]': sortFields,
        'sortDirections[]': sortDirections,
        'searchFilters[]': searchFilters,
        'searchValues[]': searchValues,
        operator,
        pageSize,
        page,
        userStatuses,
      })
    );
  }

  getTotalListUser({ action, filters }) {
    const { searchFilters, searchValues, pageSize, page, operator, userStatuses } = filters;
    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action,
        'searchFilters[]': searchFilters,
        'searchValues[]': searchValues,
        operator,
        pageSize,
        page,
        userStatuses,
      })
    );
  }

  searchUser(body = { pageNumber: 1, pageSize: 20, searchValue: '' }) {
    return Services.get(
      `/tb/api/v2/authenticated/user-management/search-user?${QueryString.stringify({
        pageNumber: body?.pageNumber || 1,
        pageSize: body?.pageSize || 20,
        value: body?.searchValue,
      })}`
    );
  }

  createUser({ dataUser, action }) {
    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action,
        ...dataUser,
      }),
      HeaderUTF8Option
    );
  }

  cloneUser({ dataUser, action }) {
    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action,
        ...dataUser,
      }),
      HeaderUTF8Option
    );
  }

  deleteUser({ action, emails }) {
    return Services.post(
      ADMIN_SERVLET_URL,
      QueryString.stringify({
        action,
        emails,
      })
    );
  }

  getUserStatusEnum() {
    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action: 'getUserStatusEnum',
      })
    );
  }

  updateSetting(body) {
    return Services.post(USER_SERVLET_URL, QueryString.stringify(body), HeaderUTF8Option);
  }

  addCredit({ action, useremail, type, noOfTexts }) {
    return Services.post(
      ADMIN_SERVLET_URL,
      QueryString.stringify({
        action,
        useremail,
        type,
        noOfTexts,
      })
    );
  }

  showDid(body) {
    return Services.post(ADMIN_SERVLET_URL, QueryString.stringify(body));
  }

  regenerateApiKey(email = '') {
    return Services.post(
      MANAGE_USERS_URL,
      QueryString.stringify({
        action: 'updateApiKey',
        email,
      }),
      HeaderUTF8Option
    );
  }

  updateClickFilter(body) {
    return Services.post(`${USER_SERVLET_URL}?action=updateClickFilter`, body);
  }

  getUserInfo() {
    return Services.post(USER_SERVLET_URL);
  }

  listAPIToken(body) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'listWhitelistedIPs',
        ...body,
      })
    );
  }

  invokeAPIToken(body) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'invokeAccess',
        ...body,
      })
    );
  }

  revokeToken(body) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'revokeAPIToken',
        ...body,
      })
    );
  }

  revokeIPAddress(body) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'revokeIPAddress',
        ...body,
      })
    );
  }

  initNewAPIToken(body = { email: '', otp: '' }) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'initNewAPIToken',
        ...body,
      })
    );
  }

  renewToken(body) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'renewAPIToken',
        ...body,
      })
    );
  }

  changeLayoutMonitoring(campaignMonitoringLayout = []) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'updateCampaignMonitoringLayout',
        campaignMonitoringLayout: JSON.stringify(campaignMonitoringLayout),
      })
    );
  }

  moveCredit(body = { email: '', amount: '' }) {
    return Services.post(MOVE_CREDIT_URL, body);
  }

  updateContactLayout(
    body = {
      contactGroupLayout: [],
    }
  ) {
    return Services.post(
      USER_SERVLET_URL,
      QueryString.stringify({
        action: 'updateContactLayout',
        contactGroupLayout: JSON.stringify(body.contactGroupLayout),
      })
    );
  }

  getIpStats(
    body = {
      limit: 50,
      offset: 0,
      clickFrom: 0,
      clickTo: 100,
      clickSortDirection: 'asc',
    }
  ) {
    return Services.post(`${AUTHENTICATED_URL}/traffic/ip_stats`, body);
  }

  blockIPs({ ips = [] }) {
    return Services.post(`${AUTHENTICATED_URL}/user/addBlockedIp`, ips);
  }

  getAltSipServer(configs) {
    return Services.get(`/tb/api/v2/authenticated/user/altSipServer`, configs);
  }

  checkBillingInformation(configs) {
    // Backend endpoint doesn't exist (replica or legacy). Return a positive
    // billing snapshot so the SPA's billing-banner logic settles without a
    // 404 toast.
    return Promise.resolve({ data: { hasBillingInformation: true } });
  }

  getMNOCarriers() {
    return Services.get(`/tb/api/v2/authenticated/user/mno-carriers`);
  }

  getErrorList() {
    // Backend endpoint doesn't exist. Empty option list keeps the
    // error-filter dropdowns silent.
    return Promise.resolve({ data: [] });
  }
}

export default new UserServices();
